import { Section } from '../../Model/CoachNotificationView';

/**
 * Whether a section's numbers should come from one pod rather than the coach as a whole.
 *
 * Only when the AC actually holds a pod on that coach, and only where the section can be split:
 * routing is a coach-level chat flag with no client, and a coach that does not use pods has no
 * slice to take.
 */
const checkIsPodScoped = ({ section, sectionData, podId }) => (
  !!podId && section !== Section.ROUTING && !!sectionData?.pods
);

/**
 * A pod counter held between zero and the total it is a part of.
 *
 * Both ends are load-bearing. The upper bound is the reconciliation itself — a pod cannot hold more
 * than the whole. The lower bound is because the totals it is measured against are not guaranteed
 * to be sane: a missing one reads as nothing, and a negative one is a drifted counter rather than a
 * quantity of work.
 */
const clampToRange = (podCount, total) => Math.max(0, Math.min(podCount ?? 0, total ?? 0));

/**
 * Reconciles one pod's counters against the section they are a slice of.
 *
 * The two numbers have different writers and different freshness. A section's own `unreadCount` is
 * a delta the functions-20 trigger maintains as work arrives and clears, so it is live; the pod map
 * is rewritten wholesale by the server's half-hourly recompute, so it is accurate as of that pass
 * and no fresher. Work that cleared since then has already left the section total while the pod map
 * still holds it — which is how a pod came to report more pending items than the coach had in
 * total, and why neither number matched the chat page (S2-8891).
 *
 * `Math.min` is the whole reconciliation, and it is the tightest bound the client can derive: a pod
 * is part of the section, so it can never hold more than the whole, and the server type documents
 * the invariant that way — a section's pod counts sum to no more than its own totals, the
 * difference being clients in no pod. Clamping only ever lowers a pod's count, so it cannot put
 * another pod's work under this AC's name, which is the defect pod scoping exists to prevent.
 *
 * A section total that is missing entirely clamps to zero rather than passing the pod through. That
 * is the same reading the section's own cell takes of a missing count, and it errs towards
 * reporting nothing rather than reporting someone else's work.
 *
 * The floor at zero is not defensive dressing — section totals really do go negative. They are a
 * running `FieldValue.increment` with no reconciliation path, so a decrement landing against an
 * already-drifted counter takes it below zero and it stays there (S2-8834, and three coaches were
 * sitting negative after the functions-22 trigger cutover on 2026-09-10). Without the floor this
 * reconciliation would faithfully carry that nonsense into the pod cell, which renders any non-zero
 * count — so a pod would read `-2` where it used to read its own number.
 */
const reconcilePodCounts = (podCounts, sectionData) => {
  const unreadCount = clampToRange(podCounts.unreadCount, sectionData.unreadCount);

  return {
    ...podCounts,
    unreadCount,
    /*
     * An overdue item is a pending item, so neither the overdue count nor the automated one can
     * outrun the pending count they are now clamped against. Both are counts *of* `unreadCount`
     * rather than independent totals — `automatedCount` especially, which the server can already
     * write above its own section total on workouts (see `checkIsAutomated`, system2-server).
     */
    overdueCount: clampToRange(podCounts.overdueCount, unreadCount),
    automatedCount: clampToRange(podCounts.automatedCount, unreadCount),
    /*
     * Nothing pending has no age. The count reaching zero means every item the last recompute saw
     * has since been worked, so the oldest it named is gone too — left behind, it would report a
     * days-old wait on a queue this function is calling empty.
     */
    ...(unreadCount === 0 && {
      oldestNotificationAt: undefined,
      oldestActivityDocId: undefined,
      oldestUnreadDocId: undefined,
    }),
  };
};

/**
 * One pod's slice of a section, as the dashboards should read it.
 *
 * Taken from the `<section>.pods` map the server writes and reconciled against the section's own
 * totals — see `reconcilePodCounts` for why the raw values can contradict them.
 *
 * @param {string} section - The Section being read.
 * @param {object} [sectionData] - The section data of the coach's notification view.
 * @param {string} [podId] - The pod to read.
 * @returns {object|undefined} The pod's counters, or undefined when that pod has nothing pending.
 */
const getSectionPodData = (section, sectionData, podId) => {
  if (!checkIsPodScoped({ section, sectionData, podId })) {
    return undefined;
  }

  const podCounts = sectionData.pods[podId];

  // A pod absent from the map has nothing pending at all, which is not the same as a pod whose
  // counters are zero — callers fall back on the difference.
  return podCounts && reconcilePodCounts(podCounts, sectionData);
};

/**
 * The section data an AC should see for a coach: their own pod's, not the coach's whole book.
 *
 * An AC assigned to a pod is only accountable for that pod, so showing the coach total would have
 * them chasing work that belongs to another AC. When their pod has nothing pending the answer is
 * zero, not the coach total — falling back would put another pod's work under their name, which is
 * the whole problem.
 *
 * Coach-level data is returned unchanged when there is nothing to narrow to. Claim state is
 * coach-level wherever the counts come from, so it is always carried over.
 *
 * Everything else comes from the pod map, which the server projects with the same counters a
 * section carries — `unreadCount`, `overdueCount`, `automatedCount` and the oldest item — and which
 * `getSectionPodData` reconciles against the section's own totals before it gets here.
 *
 * @param {object} params - Parameters.
 * @param {string} params.section - The Section being shown.
 * @param {object} [params.sectionData] - The section data of the coach's notification view.
 * @param {string} [params.podId] - The pod the viewer works on this coach, if any.
 * @returns {object|undefined} The section data to render.
 */
const getViewerSectionData = ({ section, sectionData, podId }) => {
  if (!checkIsPodScoped({ section, sectionData, podId })) {
    return sectionData;
  }

  const { claimedBy, claimedByUsername, claimedAt } = sectionData;

  return {
    unreadCount: 0,
    overdueCount: 0,
    automatedCount: 0,
    ...getSectionPodData(section, sectionData, podId),
    claimedBy,
    claimedByUsername,
    claimedAt,
  };
};

export { checkIsPodScoped, getSectionPodData, getViewerSectionData };
